import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { buildFamilyTrees, calculateTreeLayout } from '../utils/treeBuilder';
import { calculateForceLayout } from '../utils/forceGraphLayout';
import { getUploadUrl } from '../utils/config';
import AddRelativeModal from './AddRelativeModal';
import { useData } from '../contexts/DataContext';
import { filterByDegree, filterRelationships } from '../utils/degreeCalculator';
import './FamilyTree.css';

function TreeToolbar({ members, focalPersonId, onFocalPersonChange, degreeFilter, onDegreeFilterChange, layoutMode, onLayoutModeChange }) {
  const { t } = useTranslation();

  // Sort members alphabetically for dropdown
  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [members]);

  return (
    <div className="family-tree-toolbar">
      <div className="toolbar-group">
        <label htmlFor="focal-person-select">{t('tree.focus')}:</label>
        <select
          id="focal-person-select"
          value={focalPersonId || ''}
          onChange={(e) => onFocalPersonChange(parseInt(e.target.value, 10))}
          className="toolbar-select"
        >
          {sortedMembers.map(member => (
            <option key={member.id} value={member.id}>
              {member.first_name} {member.last_name}
            </option>
          ))}
        </select>
      </div>
      <div className="toolbar-group">
        <label htmlFor="degree-filter-select">{t('tree.show')}:</label>
        <select
          id="degree-filter-select"
          value={degreeFilter === null ? 'all' : degreeFilter}
          onChange={(e) => onDegreeFilterChange(e.target.value === 'all' ? null : parseInt(e.target.value, 10))}
          className="toolbar-select"
        >
          <option value="1">{t('tree.degree', { count: 1 })}</option>
          <option value="2">{t('tree.degree', { count: 2 })}</option>
          <option value="3">{t('tree.degree', { count: 3 })}</option>
          <option value="4">{t('tree.degree', { count: 4 })}</option>
          <option value="5">{t('tree.degree', { count: 5 })}</option>
          <option value="all">{t('tree.all')}</option>
        </select>
      </div>
      <div className="toolbar-group">
        <label htmlFor="layout-mode-select">{t('tree.layout')}:</label>
        <select
          id="layout-mode-select"
          value={layoutMode}
          onChange={(e) => onLayoutModeChange(e.target.value)}
          className="toolbar-select"
        >
          <option value="graph">{t('tree.graph')}</option>
          <option value="tree">{t('tree.treeLayout')}</option>
        </select>
      </div>
    </div>
  );
}

function FamilyTree({ members, relationships, currentUser, onRelativeAdded, viewState, onViewStateChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const { focalPersonId, setFocalPerson, degreeFilter, setDegreeFilter, memberDegrees, layoutMode, setLayoutMode } = useData();

  // Filter members and relationships by degree
  const { filteredMembers, filteredRelationships } = useMemo(() => {
    const filtered = filterByDegree(members, memberDegrees, degreeFilter);
    const memberIdSet = new Set(filtered.map(m => m.id));
    const filteredRels = filterRelationships(relationships, memberIdSet);
    return { filteredMembers: filtered, filteredRelationships: filteredRels };
  }, [members, relationships, memberDegrees, degreeFilter]);

  const trees = useMemo(() => {
    return buildFamilyTrees(filteredMembers, filteredRelationships);
  }, [filteredMembers, filteredRelationships]);

  const handleAddRelative = useCallback((member) => {
    setSelectedMember(member);
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedMember(null);
  }, []);

  const handleRelativeAdded = useCallback(() => {
    setModalOpen(false);
    setSelectedMember(null);
    if (onRelativeAdded) {
      onRelativeAdded();
    }
  }, [onRelativeAdded]);

  const handleMemberClick = useCallback((member) => {
    navigate(`/member/${member.id}`);
  }, [navigate]);

  if (members.length === 0) {
    return (
      <div className="tree-empty-state">
        <p>{t('tree.emptyState')}</p>
      </div>
    );
  }

  return (
    <div className="family-tree-container">
      <TreeToolbar
        members={members}
        focalPersonId={focalPersonId}
        onFocalPersonChange={setFocalPerson}
        degreeFilter={degreeFilter}
        onDegreeFilterChange={setDegreeFilter}
        layoutMode={layoutMode}
        onLayoutModeChange={setLayoutMode}
      />
      {trees.map((tree, treeIndex) => (
        <TreeView
          key={treeIndex}
          tree={tree}
          memberDegrees={memberDegrees}
          focalPersonId={focalPersonId}
          currentUser={currentUser}
          isFirstTree={treeIndex === 0}
          onAddRelative={handleAddRelative}
          onMemberClick={handleMemberClick}
          viewState={viewState}
          onViewStateChange={onViewStateChange}
          layoutMode={layoutMode}
        />
      ))}
      {modalOpen && selectedMember && (
        <AddRelativeModal
          member={selectedMember}
          onClose={handleModalClose}
          onSave={handleRelativeAdded}
        />
      )}
    </div>
  );
}

function RelationshipLegend({ isOpen, onToggle }) {
  const { t } = useTranslation();

  return (
    <div className={`relationship-legend ${isOpen ? 'open' : ''}`}>
      <button
        className="legend-toggle"
        onClick={onToggle}
        title={t('tree.legend')}
      >
        <span className="legend-icon">?</span>
      </button>
      {isOpen && (
        <div className="legend-content">
          <h4>{t('tree.legend')}</h4>
          <div className="legend-items">
            <div className="legend-item">
              <svg width="40" height="20" viewBox="0 0 40 20">
                <defs>
                  <marker id="legend-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto">
                    <path d="M 0 0 L 10 5 L 0 10 z" className="arrow-marker" />
                  </marker>
                </defs>
                <line x1="2" y1="10" x2="32" y2="10" className="connection parent-child" markerEnd="url(#legend-arrow)" />
              </svg>
              <span>{t('tree.legendParentChild')}</span>
            </div>
            <div className="legend-item">
              <svg width="40" height="20" viewBox="0 0 40 20">
                <line x1="2" y1="8" x2="38" y2="8" className="connection spouse" />
                <line x1="2" y1="12" x2="38" y2="12" className="connection spouse" />
              </svg>
              <span>{t('tree.legendSpouse')}</span>
            </div>
            <div className="legend-item">
              <svg width="40" height="20" viewBox="0 0 40 20">
                <path d="M 2 15 Q 20 0 38 15" className="connection sibling" fill="none" />
              </svg>
              <span>{t('tree.legendSibling')}</span>
            </div>
            <div className="legend-item">
              <svg width="40" height="20" viewBox="0 0 40 20">
                <defs>
                  <marker id="legend-circle" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="4" markerHeight="4">
                    <circle cx="5" cy="5" r="4" className="circle-marker" />
                  </marker>
                </defs>
                <path d="M 2 15 Q 20 0 38 15" className="connection co-parent" fill="none" markerMid="url(#legend-circle)" />
                <circle cx="20" cy="5" r="3" className="circle-marker" />
              </svg>
              <span>{t('tree.legendCoParent')}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TreeView({ tree, memberDegrees, focalPersonId, currentUser, onAddRelative, onMemberClick, viewState, onViewStateChange, layoutMode }) {
  const { t } = useTranslation();
  const svgRef = useRef(null);
  const [closestMember, setClosestMember] = useState(null);
  const [plusIconPos, setPlusIconPos] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [legendOpen, setLegendOpen] = useState(false);

  // Drag state for movable nodes
  const [positionOverrides, setPositionOverrides] = useState(new Map());
  const [dragCandidate, setDragCandidate] = useState(null); // {memberId, startX, startY, startNodeX, startNodeY}
  const [draggingNode, setDraggingNode] = useState(null);
  const DRAG_THRESHOLD = 5; // pixels to move before it becomes a drag

  const setZoom = useCallback((updater) => {
    if (onViewStateChange) {
      onViewStateChange(prev => ({
        ...prev,
        zoom: typeof updater === 'function' ? updater(prev.zoom) : updater
      }));
    }
  }, [onViewStateChange]);

  const setPan = useCallback((updater) => {
    if (onViewStateChange) {
      onViewStateChange(prev => ({
        ...prev,
        pan: typeof updater === 'function' ? updater(prev.pan) : updater
      }));
    }
  }, [onViewStateChange]);

  const basePositions = useMemo(() => {
    if (layoutMode === 'graph') {
      return calculateForceLayout(tree, memberDegrees, focalPersonId);
    }
    return calculateTreeLayout(tree, memberDegrees, focalPersonId);
  }, [tree, memberDegrees, focalPersonId, layoutMode]);

  // Merge base positions with manual overrides
  const positions = useMemo(() => {
    const result = new Map(basePositions);
    positionOverrides.forEach((pos, id) => result.set(id, pos));
    return result;
  }, [basePositions, positionOverrides]);

  const CIRCLE_RADIUS = 40;
  const PLUS_ICON_RADIUS = 15;
  const INTERACTION_DISTANCE = 80; // Distance from circle center to trigger plus icon
  const EDGE_PROXIMITY_THRESHOLD = 0.1; // Show plus button only when cursor is within this fraction of radius from edge (0.1 = 10%)
  const MIN_ZOOM = 0.2;
  const MAX_ZOOM = 2;
  const ZOOM_STEP = 0.1;
  const NODE_HEIGHT = 200; // Must match treeBuilder.js
  const MAX_GENERATIONS = 5;
  const MAX_NAME_WIDTH = CIRCLE_RADIUS * 2 * 1.25; // 125% of node diameter = 100px

  // Helper function to truncate text with ellipsis
  const truncateText = useCallback((text, maxWidth) => {
    if (!text) return '';
    // Approximate character width for font-size 14px, font-weight 500
    // This is an estimate: actual width varies by character
    const avgCharWidth = 7.5; // pixels per character
    const maxChars = Math.floor(maxWidth / avgCharWidth);
    if (text.length > maxChars && maxChars > 0) {
      return text.substring(0, Math.max(1, maxChars - 1)) + '…';
    }
    return text;
  }, []);

  // Get focus ring opacity based on degree
  const getFocusRingOpacity = useCallback((memberId) => {
    if (!focalPersonId) return 0;
    const info = memberDegrees.get(memberId);
    if (!info) return 0;

    switch (info.degree) {
      case 0: return 1;      // Focal person: 100%
      case 1: return 0.5;    // Degree 1: 50%
      case 2: return 0.2;    // Degree 2: 20%
      case 3: return 0.1;    // Degree 3: 10%
      default: return 0;     // Degree 4+: hidden
    }
  }, [memberDegrees, focalPersonId]);

  // Calculate base SVG viewBox dimensions
  const { baseMinX, baseMaxX, baseMinY, baseMaxY } = useMemo(() => {
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    positions.forEach(({ x, y }) => {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    });

    // Minimal padding - content should fill the view
    const padding = 60;

    return {
      baseMinX: minX - padding,
      baseMaxX: maxX + padding,
      baseMinY: minY - padding,
      baseMaxY: maxY + padding,
    };
  }, [positions]);

  // Track container height for zoom calculations
  const [containerHeight, setContainerHeight] = useState(600);

  useEffect(() => {
    const updateHeight = () => {
      if (svgRef.current) {
        const rect = svgRef.current.parentElement.getBoundingClientRect();
        setContainerHeight(rect.height);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Calculate optimal initial zoom to fit up to MAX_GENERATIONS with consistent margins
  const optimalZoom = useMemo(() => {
    const treeHeight = baseMaxY - baseMinY;

    // Show up to MAX_GENERATIONS (5) of content
    const contentToShow = Math.min(treeHeight, MAX_GENERATIONS * NODE_HEIGHT);

    // Add 1.5 generations worth of margin (0.75 top + 0.75 bottom)
    const totalHeightToShow = contentToShow + (1.5 * NODE_HEIGHT);

    // Zoom calculation: container height / content height
    // Higher zoom = more zoomed in (showing less content)
    const calculatedZoom = containerHeight / totalHeightToShow;

    // Clamp between MIN and MAX zoom
    return Math.max(MIN_ZOOM, Math.min(calculatedZoom, MAX_ZOOM));
  }, [baseMinY, baseMaxY, containerHeight]);

  // Use external view state if provided, otherwise use optimal zoom
  const zoom = viewState?.zoom ?? optimalZoom;
  const pan = viewState?.pan ?? { x: 0, y: 0 };

  // Apply zoom and pan to viewBox
  const { viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight } = useMemo(() => {
    const baseWidth = baseMaxX - baseMinX;
    const baseHeight = baseMaxY - baseMinY;
    const centerX = (baseMinX + baseMaxX) / 2;
    const centerY = (baseMinY + baseMaxY) / 2;

    // Inverse zoom: zoom < 1 makes viewBox larger (elements appear smaller)
    const zoomedWidth = baseWidth / zoom;
    const zoomedHeight = baseHeight / zoom;

    return {
      viewBoxX: centerX - zoomedWidth / 2 + pan.x,
      viewBoxY: centerY - zoomedHeight / 2 + pan.y,
      viewBoxWidth: zoomedWidth,
      viewBoxHeight: zoomedHeight,
    };
  }, [baseMinX, baseMaxX, baseMinY, baseMaxY, zoom, pan]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoom(z => Math.min(z + ZOOM_STEP, MAX_ZOOM));
  }, [setZoom]);

  const handleZoomOut = useCallback(() => {
    setZoom(z => Math.max(z - ZOOM_STEP, MIN_ZOOM));
  }, [setZoom]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setZoom(z => Math.min(z + ZOOM_STEP, MAX_ZOOM));
    } else {
      setZoom(z => Math.max(z - ZOOM_STEP, MIN_ZOOM));
    }
  }, [setZoom]);

  // Reset view to center with optimal zoom and focal person
  const handleResetView = useCallback(() => {
    setZoom(optimalZoom);
    setPositionOverrides(new Map()); // Clear manual position overrides

    // Center on focal person if available
    if (focalPersonId) {
      const focalPos = basePositions.get(focalPersonId);
      if (focalPos) {
        const centerX = (baseMinX + baseMaxX) / 2;
        const centerY = (baseMinY + baseMaxY) / 2;
        const newPanX = focalPos.x - centerX;
        const newPanY = focalPos.y - centerY;
        setPan({ x: newPanX, y: newPanY });
        return;
      }
    }

    // Fallback to tree center
    setPan({ x: 0, y: 0 });
  }, [setZoom, setPan, optimalZoom, focalPersonId, basePositions, baseMinX, baseMaxX, baseMinY, baseMaxY]);

  // Center on focal person when they change or on initial load
  useEffect(() => {
    if (!focalPersonId) return;

    // Clear position overrides when focal person changes
    setPositionOverrides(new Map());

    const focalPos = basePositions.get(focalPersonId);
    if (focalPos) {
      // Reset pan to center on focal person
      // The pan offset is relative to center, so we need to calculate the offset
      const centerX = (baseMinX + baseMaxX) / 2;
      const centerY = (baseMinY + baseMaxY) / 2;

      // Pan so focal person is at center of the viewport
      const newPanX = focalPos.x - centerX;
      const newPanY = focalPos.y - centerY;

      setPan({ x: newPanX, y: newPanY });

      // Also set zoom to optimal if not already set
      if (!viewState?.zoom) {
        setZoom(optimalZoom);
      }
    }
  }, [focalPersonId, basePositions, baseMinX, baseMaxX, baseMinY, baseMaxY, setPan, setZoom, optimalZoom, viewState?.zoom]);

  // Pan handlers
  const handleMouseDown = useCallback((e) => {
    // Only pan with middle mouse button or when holding space (we'll use right-click for simplicity)
    // Or pan with left click when not near any member
    if (e.button === 1 || (e.button === 0 && !closestMember)) {
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      e.preventDefault();
    }
  }, [closestMember]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);

    // Handle drag end or click navigation
    if (draggingNode) {
      // Drag completed - position is already saved in positionOverrides
      setDraggingNode(null);
      setDragCandidate(null);
    } else if (dragCandidate) {
      // No drag occurred (didn't move enough) - treat as click
      onMemberClick(dragCandidate.member);
      setDragCandidate(null);
    }
  }, [draggingNode, dragCandidate, onMemberClick]);

  const handlePanMove = useCallback((e) => {
    if (!isPanning) return;

    const svg = svgRef.current;
    if (!svg) return;

    // Calculate the pan delta in SVG coordinates
    const rect = svg.getBoundingClientRect();
    const scaleX = viewBoxWidth / rect.width;
    const scaleY = viewBoxHeight / rect.height;

    const dx = (e.clientX - panStart.x) * scaleX;
    const dy = (e.clientY - panStart.y) * scaleY;

    setPan(p => ({ x: p.x - dx, y: p.y - dy }));
    setPanStart({ x: e.clientX, y: e.clientY });
  }, [isPanning, panStart, viewBoxWidth, viewBoxHeight, setPan]);

  // Collect all connections (edges)
  const connections = useMemo(() => {
    const edges = [];

    tree.members.forEach(member => {
      const pos1 = positions.get(member.id);
      if (!pos1) return;

      // Parent-child connections (solid lines)
      member.children.forEach(childId => {
        const pos2 = positions.get(childId);
        if (pos2) {
          edges.push({
            from: { x: pos1.x, y: pos1.y },
            to: { x: pos2.x, y: pos2.y },
            type: 'parent-child'
          });
        }
      });

      // Sibling connections (dotted lines)
      member.siblings.forEach(siblingId => {
        // Only draw once (from lower ID to higher ID to avoid duplicates)
        if (member.id < siblingId) {
          const pos2 = positions.get(siblingId);
          if (pos2) {
            edges.push({
              from: { x: pos1.x, y: pos1.y },
              to: { x: pos2.x, y: pos2.y },
              type: 'sibling'
            });
          }
        }
      });

      // Spouse connections (double lines)
      member.spouses.forEach(spouseId => {
        // Only draw once (from lower ID to higher ID to avoid duplicates)
        if (member.id < spouseId) {
          const pos2 = positions.get(spouseId);
          if (pos2) {
            edges.push({
              from: { x: pos1.x, y: pos1.y },
              to: { x: pos2.x, y: pos2.y },
              type: 'spouse'
            });
          }
        }
      });

      // Co-parent connections (dotted lines)
      member.coparents.forEach(coparentId => {
        // Only draw once (from lower ID to higher ID to avoid duplicates)
        if (member.id < coparentId) {
          const pos2 = positions.get(coparentId);
          if (pos2) {
            edges.push({
              from: { x: pos1.x, y: pos1.y },
              to: { x: pos2.x, y: pos2.y },
              type: 'co-parent'
            });
          }
        }
      });
    });

    return edges;
  }, [tree.members, positions]);

  // Convert screen coordinates to SVG coordinates
  const screenToSVG = useCallback((screenX, screenY) => {
    const svg = svgRef.current;
    if (!svg) return null;

    const pt = svg.createSVGPoint();
    pt.x = screenX;
    pt.y = screenY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: svgP.x, y: svgP.y };
  }, []);

  // Handle mouse move to track position and find closest member
  const handleMouseMove = useCallback((e) => {
    const svgCoords = screenToSVG(e.clientX, e.clientY);
    if (!svgCoords) return;

    // Find the closest member within interaction distance
    let closest = null;
    let closestDist = Infinity;

    tree.members.forEach(member => {
      const pos = positions.get(member.id);
      if (!pos) return;

      const dx = svgCoords.x - pos.x;
      const dy = svgCoords.y - pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < INTERACTION_DISTANCE && dist < closestDist) {
        closest = member;
        closestDist = dist;
      }
    });

    setClosestMember(closest);

    // Calculate plus icon position on the circle edge
    // Only show if cursor is near the edge (not in the center)
    if (closest) {
      const pos = positions.get(closest.id);
      const dx = svgCoords.x - pos.x;
      const dy = svgCoords.y - pos.y;
      const distFromCenter = Math.sqrt(dx * dx + dy * dy);

      // Check if cursor is near the edge of the circle
      // Edge zone is from (CIRCLE_RADIUS - threshold) to INTERACTION_DISTANCE
      const edgeThreshold = CIRCLE_RADIUS * EDGE_PROXIMITY_THRESHOLD;
      const isNearEdge = distFromCenter >= (CIRCLE_RADIUS - edgeThreshold);

      if (isNearEdge) {
        // Calculate angle from center to mouse position
        // This ensures plus icon is always on the edge, just rotating around
        const angle = Math.atan2(dy, dx);

        // Position plus icon on the edge of the circle (always at CIRCLE_RADIUS distance)
        const plusX = pos.x + Math.cos(angle) * CIRCLE_RADIUS;
        const plusY = pos.y + Math.sin(angle) * CIRCLE_RADIUS;

        setPlusIconPos({ x: plusX, y: plusY, memberId: closest.id });
      } else {
        setPlusIconPos(null);
      }
    } else {
      setPlusIconPos(null);
    }
  }, [screenToSVG, tree.members, positions, CIRCLE_RADIUS, INTERACTION_DISTANCE, EDGE_PROXIMITY_THRESHOLD]);

  const handleMouseLeave = useCallback(() => {
    setClosestMember(null);
    setPlusIconPos(null);
    setIsPanning(false);
    setDragCandidate(null);
    setDraggingNode(null);
  }, []);

  // Handle node mousedown for drag initiation
  const handleNodeMouseDown = useCallback((e, member) => {
    if (e.button !== 0) return; // Only left click
    e.stopPropagation();
    e.preventDefault();

    const pos = positions.get(member.id);
    if (!pos) return;

    setDragCandidate({
      memberId: member.id,
      member,
      startX: e.clientX,
      startY: e.clientY,
      startNodeX: pos.x,
      startNodeY: pos.y,
    });
  }, [positions]);

  const handlePlusClick = useCallback((e) => {
    e.stopPropagation();
    if (closestMember) {
      onAddRelative(closestMember);
    }
  }, [closestMember, onAddRelative]);

  // Combined mouse move handler for pan, plus icon, and node dragging
  const handleCombinedMouseMove = useCallback((e) => {
    // Handle node dragging
    if (draggingNode) {
      const svg = svgRef.current;
      if (!svg) return;

      // Calculate delta in screen coords, then convert to SVG coords
      const rect = svg.getBoundingClientRect();
      const scaleX = viewBoxWidth / rect.width;
      const scaleY = viewBoxHeight / rect.height;

      const dx = (e.clientX - dragCandidate.startX) * scaleX;
      const dy = (e.clientY - dragCandidate.startY) * scaleY;

      const newX = dragCandidate.startNodeX + dx;
      const newY = dragCandidate.startNodeY + dy;

      setPositionOverrides(prev => {
        const updated = new Map(prev);
        updated.set(draggingNode, { x: newX, y: newY });
        return updated;
      });
      return;
    }

    // Check if we should promote dragCandidate to draggingNode
    if (dragCandidate && !draggingNode) {
      const dx = e.clientX - dragCandidate.startX;
      const dy = e.clientY - dragCandidate.startY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > DRAG_THRESHOLD) {
        setDraggingNode(dragCandidate.memberId);
        setPlusIconPos(null); // Hide plus icon while dragging
        return;
      }
    }

    if (isPanning) {
      handlePanMove(e);
    } else {
      handleMouseMove(e);
    }
  }, [isPanning, handlePanMove, handleMouseMove, draggingNode, dragCandidate, viewBoxWidth, viewBoxHeight, DRAG_THRESHOLD]);

  const toggleLegend = useCallback(() => {
    setLegendOpen(prev => !prev);
  }, []);

  return (
    <div className="tree-view">
      <div className="zoom-controls">
        <button onClick={handleZoomIn} className="zoom-btn" title={t('tree.zoomIn')}>+</button>
        <span className="zoom-level">{Math.round(zoom * 100)}%</span>
        <button onClick={handleZoomOut} className="zoom-btn" title={t('tree.zoomOut')}>−</button>
        <button onClick={handleResetView} className="zoom-btn reset-btn" title={t('tree.resetView')}>⟲</button>
      </div>
      <RelationshipLegend isOpen={legendOpen} onToggle={toggleLegend} />
      <svg
        ref={svgRef}
        className={`tree-svg ${isPanning ? 'panning' : ''}`}
        viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseMove={handleCombinedMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
      >
        {/* SVG Definitions for markers */}
        <defs>
          {/* Arrow marker for parent-child relationships (points to child) */}
          <marker
            id="arrow-child"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" className="arrow-marker" />
          </marker>
          {/* Circle marker for co-parent relationships */}
          <marker
            id="circle-coparent"
            viewBox="0 0 10 10"
            refX="5"
            refY="5"
            markerWidth="6"
            markerHeight="6"
          >
            <circle cx="5" cy="5" r="4" className="circle-marker" />
          </marker>
        </defs>
        {/* Draw connections first (so they appear behind nodes) */}
        <g className="connections">
          {connections.map((conn, index) => {
            // Parent-child: solid line with arrow pointing to child
            if (conn.type === 'parent-child') {
              // Calculate line shortened to not overlap with node circles
              const dx = conn.to.x - conn.from.x;
              const dy = conn.to.y - conn.from.y;
              const len = Math.sqrt(dx * dx + dy * dy);
              const ux = dx / len;
              const uy = dy / len;
              // Shorten by circle radius + arrow size
              const startX = conn.from.x + ux * CIRCLE_RADIUS;
              const startY = conn.from.y + uy * CIRCLE_RADIUS;
              const endX = conn.to.x - ux * (CIRCLE_RADIUS + 8);
              const endY = conn.to.y - uy * (CIRCLE_RADIUS + 8);

              return (
                <line
                  key={index}
                  x1={startX}
                  y1={startY}
                  x2={endX}
                  y2={endY}
                  className="connection parent-child"
                  markerEnd="url(#arrow-child)"
                />
              );
            }

            // Spouse: double parallel lines
            if (conn.type === 'spouse') {
              const dx = conn.to.x - conn.from.x;
              const dy = conn.to.y - conn.from.y;
              const len = Math.sqrt(dx * dx + dy * dy);
              // Perpendicular unit vector
              const px = -dy / len;
              const py = dx / len;
              const offset = 3; // Distance between the two lines

              // Shorten by circle radius
              const ux = dx / len;
              const uy = dy / len;
              const startX = conn.from.x + ux * CIRCLE_RADIUS;
              const startY = conn.from.y + uy * CIRCLE_RADIUS;
              const endX = conn.to.x - ux * CIRCLE_RADIUS;
              const endY = conn.to.y - uy * CIRCLE_RADIUS;

              return (
                <g key={index}>
                  <line
                    x1={startX + px * offset}
                    y1={startY + py * offset}
                    x2={endX + px * offset}
                    y2={endY + py * offset}
                    className="connection spouse"
                  />
                  <line
                    x1={startX - px * offset}
                    y1={startY - py * offset}
                    x2={endX - px * offset}
                    y2={endY - py * offset}
                    className="connection spouse"
                  />
                </g>
              );
            }

            // Sibling: dashed curved line
            if (conn.type === 'sibling') {
              const midX = (conn.from.x + conn.to.x) / 2;
              const midY = (conn.from.y + conn.to.y) / 2;
              const dx = conn.to.x - conn.from.x;
              const dy = conn.to.y - conn.from.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              const curveHeight = Math.min(distance * 0.3, 60);

              // Perpendicular direction for curve (always curve "outward" from connection line)
              // Use a consistent direction based on the perpendicular
              const perpX = -dy / distance;
              const perpY = dx / distance;

              const controlX = midX + perpX * curveHeight;
              const controlY = midY + perpY * curveHeight;

              // Shorten start/end by circle radius
              const ux = dx / distance;
              const uy = dy / distance;
              const startX = conn.from.x + ux * CIRCLE_RADIUS;
              const startY = conn.from.y + uy * CIRCLE_RADIUS;
              const endX = conn.to.x - ux * CIRCLE_RADIUS;
              const endY = conn.to.y - uy * CIRCLE_RADIUS;

              const pathD = `M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`;

              return (
                <path
                  key={index}
                  d={pathD}
                  className="connection sibling"
                  fill="none"
                />
              );
            }

            // Co-parent: dotted curved line with circle marker at midpoint
            if (conn.type === 'co-parent') {
              const midX = (conn.from.x + conn.to.x) / 2;
              const midY = (conn.from.y + conn.to.y) / 2;
              const dx = conn.to.x - conn.from.x;
              const dy = conn.to.y - conn.from.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              const curveHeight = Math.min(distance * 0.3, 60);

              // Curve in opposite direction from siblings
              const perpX = dy / distance;
              const perpY = -dx / distance;

              const controlX = midX + perpX * curveHeight;
              const controlY = midY + perpY * curveHeight;

              // Calculate midpoint on curve for the circle marker
              // For quadratic bezier at t=0.5: B(0.5) = 0.25*P0 + 0.5*P1 + 0.25*P2
              const curveMidX = 0.25 * conn.from.x + 0.5 * controlX + 0.25 * conn.to.x;
              const curveMidY = 0.25 * conn.from.y + 0.5 * controlY + 0.25 * conn.to.y;

              // Shorten start/end by circle radius
              const ux = dx / distance;
              const uy = dy / distance;
              const startX = conn.from.x + ux * CIRCLE_RADIUS;
              const startY = conn.from.y + uy * CIRCLE_RADIUS;
              const endX = conn.to.x - ux * CIRCLE_RADIUS;
              const endY = conn.to.y - uy * CIRCLE_RADIUS;

              const pathD = `M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`;

              return (
                <g key={index}>
                  <path
                    d={pathD}
                    className="connection co-parent"
                    fill="none"
                  />
                  <circle
                    cx={curveMidX}
                    cy={curveMidY}
                    r="5"
                    className="circle-marker"
                  />
                </g>
              );
            }

            // Fallback for any other connection type
            return (
              <line
                key={index}
                x1={conn.from.x}
                y1={conn.from.y}
                x2={conn.to.x}
                y2={conn.to.y}
                className={`connection ${conn.type}`}
              />
            );
          })}
        </g>

        {/* Draw nodes */}
        <g className="nodes">
          {tree.members.map(member => {
            const pos = positions.get(member.id);
            if (!pos) return null;

            const isCurrentUser = member.created_by === currentUser?.id;
            const focusRingOpacity = getFocusRingOpacity(member.id);

            const isDragging = draggingNode === member.id;

            return (
              <g
                key={member.id}
                className={`node ${isCurrentUser ? 'current-user' : ''} ${isDragging ? 'dragging' : ''}`}
                transform={`translate(${pos.x}, ${pos.y})`}
                onMouseDown={(e) => handleNodeMouseDown(e, member)}
              >
                {/* Focus ring indicator */}
                {focusRingOpacity > 0 && (
                  <circle
                    r="46"
                    className="focus-ring"
                    style={{ opacity: focusRingOpacity }}
                  />
                )}
                {/* Circle background */}
                <circle
                  r="40"
                  className="node-circle"
                />

                {/* Photo or initials */}
                {member.photo_url ? (
                  <image
                    href={getUploadUrl(member.photo_url)}
                    x="-35"
                    y="-35"
                    width="70"
                    height="70"
                    clipPath="circle(35px at center)"
                  />
                ) : (
                  <text
                    className="node-initials"
                    textAnchor="middle"
                    dy="0.3em"
                  >
                    {member.first_name?.[0]}{member.last_name?.[0]}
                  </text>
                )}

                {/* Name label below circle - split into two lines */}
                <text
                  className="node-label"
                  textAnchor="middle"
                >
                  {/* First name on first line */}
                  <tspan x="0" y="55">
                    {truncateText(member.first_name, MAX_NAME_WIDTH)}
                  </tspan>
                  {/* Last name on second line */}
                  <tspan x="0" y="70">
                    {truncateText(member.last_name, MAX_NAME_WIDTH)}
                  </tspan>
                </text>

              </g>
            );
          })}
        </g>

        {/* Plus icon that follows mouse on circle edge */}
        {plusIconPos && (
          <g
            className="plus-icon"
            transform={`translate(${plusIconPos.x}, ${plusIconPos.y})`}
            onClick={handlePlusClick}
            style={{ cursor: 'pointer' }}
          >
            <circle
              r={PLUS_ICON_RADIUS}
              className="plus-icon-circle"
            />
            <line
              x1={-PLUS_ICON_RADIUS * 0.5}
              y1="0"
              x2={PLUS_ICON_RADIUS * 0.5}
              y2="0"
              className="plus-icon-line"
            />
            <line
              x1="0"
              y1={-PLUS_ICON_RADIUS * 0.5}
              x2="0"
              y2={PLUS_ICON_RADIUS * 0.5}
              className="plus-icon-line"
            />
          </g>
        )}
      </svg>
    </div>
  );
}

export default FamilyTree;
